import { instance, streamInstance } from './api';
import { APIResponse } from 'tools/types';
import { CustomFieldSection } from 'api/home-page';

import {
    ITileInfoCertification,
    ITileInfoHeader,
    ITileInfoIcon,
    ITileInfoOwners,
    ITileInfoTopics,
    ITileInfoValue,
} from 'components/tile-info-icon/types';

import { TopicsInfo } from './editor/topics';
import { ElementDocumentType } from 'components/element-viewer';
import { IDomain, ITool } from 'store/conciergeSlice';

export interface ITestConnectionResponse {
    error?: string;
    status?: 'OK' | 'FAIL';
    message?: string;
    details?: string;
}

export interface PromptsItem {
    prompt: string;
}

export interface RecentItem {
    question: string;
    conversation_ts: number;
}

export interface FeedbackData {
    concierge_message_id: number;
    question: string;
    answer: string;
    feedback: 'like' | 'dislike';
    comment?: string;
}

export interface ITileInfoData
    extends ITileInfoIcon, ITileInfoHeader, ITileInfoCertification, ITileInfoValue, ITileInfoTopics, ITileInfoOwners {
    reporting_tool_name: string;
    external_content_name: string;
    segment_value_id: number;
    external_report_display: string;
    user_id: number;
    topics_info: TopicsInfo;
    documents: ElementDocumentType[];
    custom_fields: CustomFieldSection[];
    customFieldsDisplayOnTileInfo: { [key: string]: string };
}

export interface IReferenceData {
    editAllowed: string;
    external_report_reference_id: number;
    name: string;
    pcp_name: string;
    plugin_connection_profile_id: number;
    plugin_connection_profile_workbook_id: number;
    reporting_tool_name: string;
}

export interface IExternalResourceType {
    id: string;
    name: string;
    icon?: string;
}

export const conciergeAPI = {
    async getPrompts(): Promise<PromptsItem[]> {
        const response = await instance.get('/data/concierge/prompts');

        return response.data.data;
    },
    async getRecentChats(): Promise<RecentItem[]> {
        const response = await instance.get('/data/mock/concierge/recent-chats');

        return response.data.data;
    },

    async getAnswer(
        query: string,
        id: number,
        useStream: boolean,
        onlySearch: boolean,
        controller: AbortController,
        tools?: string[],
        extraUrlParams?: { [key: string]: string },
        customInstructions?: {
            mcp_post_processing?: string;
            llm_comment?: string;
        },
    ): Promise<any> {
        const params: { [key: string]: any } = {
            q: query,
            conversation_ts: id,
            stream: useStream ? 1 : 0,
            interface: 'react',
        };

        if (onlySearch) {
            params.only_search = 1;
        }

        if (extraUrlParams) {
            Object.assign(params, extraUrlParams);
        }

        if (tools && tools.length > 0) {
            const baseUrl = '/data/concierge/proxy';
            const { context: paramContext, q: paramQ, ...paramsData } = params;
            const baseParams = new URLSearchParams(paramsData).toString();
            const maxUrlLength = 7500;

            const selectedTools: string[] = [];

            for (const tool of tools) {
                const testToolsParam = [...selectedTools, tool].join(',');
                const estimatedUrlLength = baseUrl.length + baseParams.length + testToolsParam.length + 20;

                if (estimatedUrlLength <= maxUrlLength) {
                    selectedTools.push(tool);
                } else {
                    break;
                }
            }

            if (selectedTools.length > 0) {
                params.tools = selectedTools.join(',');
            }
        }

        const { context: contextParam, q: qParam, ...otherParams } = params;

        const filteredParams = Object.fromEntries(
            Object.entries(otherParams).filter(([, v]) => v !== undefined && v !== null),
        );
        const queryString = new URLSearchParams(filteredParams as Record<string, string>).toString();
        const postBody: {
            q?: string;
            context?: string;
            path?: string;
            custom_instructions?: { mcp_post_processing?: string; llm_comment?: string };
        } = { path: window.location.pathname };
        if (qParam) {
            postBody.q = qParam;
        }
        if (contextParam) {
            postBody.context = contextParam;
        }
        if (customInstructions) {
            postBody.custom_instructions = customInstructions;
        }

        return streamInstance.post('/data/concierge/proxy' + (queryString ? `?${queryString}` : ''), postBody, {
            signal: controller.signal,
        });
    },
    async loadMore(queryParams: string): Promise<any> {
        const params: { [key: string]: any } = {
            interface: 'react',
        };
        return streamInstance.get(`/data/concierge_more_search_results/proxy${queryParams}`, { params });
    },
    async provideFeedback(data: FeedbackData): Promise<void> {
        await instance.post('/data/concierge/feedback', data);
    },
    async getTools(): Promise<ITool[]> {
        const response = await instance.get('/data/concierge/get-tools');

        return response.data.data;
    },
    async getDomains(): Promise<IDomain[]> {
        const response = await instance.get('/data/concierge/get-domains');

        return response.data.data;
    },
    async getMappedDomains(elementId: string): Promise<string[]> {
        const response = await instance.get(`/data/concierge/get-mapped-domains/element/${elementId}`);

        return response.data.data;
    },
    async getTilesInfo(ids: string[]): Promise<ITileInfoData[]> {
        const response = await instance.get('/data/concierge/get-tiles-info', {
            params: {
                ids: ids,
            },
        });

        return response.data.data;
    },
    async getLLMLog(sessionUid: string): Promise<IReferenceData[]> {
        const response = await instance.get('/data/concierge/get-llm-log', {
            params: {
                sessionUid: sessionUid,
            },
        });

        return response.data.data;
    },
    async getLLMLogSummary(sessionUid: string): Promise<IReferenceData[]> {
        const response = await instance.get('/data/concierge/get-llm-log-summary', {
            params: {
                sessionUid: sessionUid,
            },
        });

        return response.data.data;
    },
    async getExternalReportReferenceInfo(ids: string[]): Promise<IReferenceData[]> {
        const response = await instance.get('/data/concierge/get-references-data', {
            params: {
                ids: ids,
            },
        });

        return response.data.data;
    },
    async getExternalResourceTypes(): Promise<IExternalResourceType[]> {
        const response = await instance.get('/data/admin/concierge/custom-service/resource-type/get');

        return response.data.data;
    },
    async createCustomService(resourceTypeId: string): Promise<{ redirectUrl: string }> {
        const response = await instance.post(
            `/data/admin/concierge/custom-service/resource-type/${resourceTypeId}/insert`,
        );

        return response.data;
    },
    async testResourceConnection(conciergeCustomServiceId: string): Promise<ITestConnectionResponse> {
        const response = await instance.get(
            `/data/admin/concierge/test-resource-connection/${conciergeCustomServiceId}`,
        );

        return response.data;
    },
};

export type ConciergeScreenshotData = {
    data_type: 'hchart' | 'table';
    [key: string]: any;
};

export const conciergeScreenshotAPI = {
    async loadData(hash: string): Promise<ConciergeScreenshotData> {
        const response = await instance.post<APIResponse<ConciergeScreenshotData>>('data/concierge/screenshot-data', {
            hash,
        });
        return response.data.data;
    },
};
